import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { getDefaultLocation } from '../constants/locations'
import { showToast } from '../lib/toastStore'
import { track } from '../lib/posthog'

// ─── Items ────────────────────────────────────────────────────────────────────

const COMMON_ITEMS = [


  // Household
  { id: 'toilet-paper',      name: 'Toilet Paper', emoji: '🧻', category: 'cleaning', freshness_category: 'pantry_staple' },
  { id: 'dish-soap',         name: 'Dish Soap',    emoji: '🧴', category: 'cleaning', freshness_category: 'pantry_staple' },
  { id: 'laundry-detergent', name: 'Laundry',      emoji: '🫧', category: 'cleaning', freshness_category: 'pantry_staple' },
] as const

type CommonItem = typeof COMMON_ITEMS[number]

const TRACK_FRESHNESS = ['fresh_produce', 'dairy', 'meat_fish', 'bread_bakery']

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const HOUSEHOLD_OPTIONS = [
  { value: 'just_me',        label: 'Just me',       emoji: '🛒' },
  { value: 'me_and_partner', label: 'Me & partner',  emoji: '👫' },
  { value: 'family',         label: 'Family',        emoji: '👨‍👩‍👧‍👦' },
] as const

type HouseholdSize = typeof HOUSEHOLD_OPTIONS[number]['value']

// ─── Onboarding ───────────────────────────────────────────────────────────────

export default function Onboarding() {
  const navigate = useNavigate()

  const [step,          setStep]          = useState(1)
  const [householdSize, setHouseholdSize] = useState<HouseholdSize | null>(null)
  const [shoppingDay,   setShoppingDay]   = useState(6)
  const [selectedItems, setSelectedItems] = useState<CommonItem[]>([])
  const [loading,       setLoading]       = useState(false)
  const completingRef                     = useRef(false)
  const [pageReady,     setPageReady]     = useState(false)

  // Redirect away if already onboarded (AuthGate handles unauthenticated case)
  useEffect(() => {
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { navigate('/sign-in'); return }

      const { data } = await supabase
        .from('users')
        .select('onboarding_completed')
        .eq('id', session.user.id)
        .maybeSingle()

      if (data?.onboarding_completed) {
        navigate('/home', { replace: true })
        return
      }

      setPageReady(true)
    }
    check()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!pageReady) {
    return (
      <div className="min-h-[100dvh] bg-cream flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-forest border-t-transparent animate-spin" />
      </div>
    )
  }

  const toggleItem = (item: CommonItem) => {
    setSelectedItems(prev => {
      const exists = prev.some(s => s.id === item.id)
      return exists ? prev.filter(s => s.id !== item.id) : [...prev, item]
    })
  }

  const handleComplete = async () => {
    if (loading || completingRef.current) return
    completingRef.current = true
    setLoading(true)
    console.log('=== ONBOARDING COMPLETE START ===')

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      console.log('Session:', session?.user?.id)

      // If refresh token is invalid, force re-login
      if (sessionError || !session) {
        console.log('Session invalid, clearing and redirecting')
        await supabase.auth.signOut()
        navigate('/sign-in')
        return
      }

      const userId = session.user.id

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', userId)
        .maybeSingle()

      console.log('userData:', userData)
      console.log('userError:', userError)

      const householdId = userData?.household_id

      if (householdId && selectedItems.length > 0) {
        console.log('Inserting items:', selectedItems.length)
        const { error: itemsError } = await supabase
          .from('pantry_items')
          .insert(
            selectedItems.map(item => ({
              household_id:          householdId,
              name:                  item.name,
              emoji:                 item.emoji,
              display_category:      item.category,
              freshness_category:    item.freshness_category,
              
              is_active:             true,
              confirmation_count:    0,
              location:              getDefaultLocation(item.freshness_category),
              track_expiration:      TRACK_FRESHNESS.includes(item.freshness_category),
            }))
          )
        console.log('Items insert error:', itemsError)
      }

      if (householdId) {
        const { error: householdError } = await supabase
          .from('households')
          .update({ household_size: householdSize })
          .eq('id', householdId)
        console.log('Household update error:', householdError)
      }

      console.log('Updating onboarding_completed...')
      const { data: updateData, error: updateError } = await supabase
        .from('users')
        .update({ onboarding_completed: true })
        .eq('id', userId)
        .select()

      console.log('Update result:', updateData)
      console.log('Update error:', updateError)

      if (updateError) {
        showToast('Error: ' + updateError.message)
        return
      }

      track('onboarding_completed')

      console.log('=== NAVIGATING TO HOME ===')
      window.location.replace('/home')

    } catch (err: any) {
      console.error('=== ONBOARDING ERROR ===', err)
      showToast('Error: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  // ── Step 1 — Welcome ─────────────────────────────────────────────────────

  if (step === 1) {
    return (
      <div className="min-h-[100dvh] bg-cream flex flex-col items-center justify-center px-6 text-center">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-[18px] bg-forest text-forest-ink font-serif italic text-[36px] font-semibold leading-none mb-6">
          P
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
          Welcome
        </div>
        <h1 className="font-serif text-[30px] font-normal leading-[1.05] tracking-[-0.015em] text-ink mb-3">
          Welcome to <em className="italic">Pantry</em>.
        </h1>
        <p className="font-serif text-[14px] italic text-ink-muted max-w-[320px] mb-10 leading-[1.45]">
          Never run out of anything again. Takes about a minute to set up.
        </p>
        <button
          onClick={() => setStep(2)}
          className="w-full max-w-[320px] rounded-[14px] bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em] transition"
        >
          Get started
        </button>
      </div>
    )
  }

  // ── Step 2 — Household size ───────────────────────────────────────────────

  if (step === 2) {
    return (
      <div className="min-h-[100dvh] bg-cream flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-[480px]">
          <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
            Step 1 of 3
          </div>
          <h1 className="font-serif text-[28px] font-normal leading-[1.1] tracking-[-0.015em] text-ink mb-2">
            Who does the <em className="italic">shopping</em>?
          </h1>
          <p className="font-serif text-[13px] italic text-ink-muted mb-8 leading-[1.4]">
            We'll tune the suggestions for your household.
          </p>
          <div className="flex flex-col gap-3">
            {HOUSEHOLD_OPTIONS.map(({ value, label, emoji }) => (
              <button
                key={value}
                onClick={() => { setHouseholdSize(value); setStep(3) }}
                className="w-full flex items-center gap-4 rounded-[16px] border border-border-sand bg-paper p-5 text-left transition hover:bg-cream-warm"
              >
                <span className="text-[28px] leading-none flex-shrink-0">{emoji}</span>
                <span className="font-serif text-[16px] font-medium text-ink tracking-[-0.01em]">{label}</span>
                <ChevronRight className="h-[18px] w-[18px] text-ink-muted ml-auto flex-shrink-0" strokeWidth={1.7} />
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // ── Step 3 — Shopping day ─────────────────────────────────────────────────

  if (step === 3) {
    return (
      <div className="min-h-[100dvh] bg-cream flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-[480px]">
          <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
            Step 2 of 3
          </div>
          <h1 className="font-serif text-[28px] font-normal leading-[1.1] tracking-[-0.015em] text-ink mb-2">
            When do you usually <em className="italic">shop</em>?
          </h1>
          <p className="font-serif text-[13px] italic text-ink-muted mb-8 leading-[1.4]">
            We'll remind you the day before.
          </p>

          <div className="grid grid-cols-7 gap-1.5">
            {DAYS.map((day, index) => (
              <button
                key={day}
                onClick={() => setShoppingDay(index)}
                className={`py-3 rounded-[12px] font-serif text-[13px] font-medium transition-colors ${
                  shoppingDay === index
                    ? 'bg-forest text-forest-ink'
                    : 'bg-paper border border-border-sand text-ink'
                }`}
              >
                {day}
              </button>
            ))}
          </div>

          <button
            onClick={() => setStep(4)}
            className="w-full mt-10 rounded-[14px] bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em] transition"
          >
            Continue
          </button>
        </div>
      </div>
    )
  }

  // ── Step 4 — Item selection ───────────────────────────────────────────────

  const count    = selectedItems.length
  const progress = Math.min(count / 10, 1)

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">

      {/* Sticky header */}
      <div className="sticky top-0 bg-cream px-4 pt-8 pb-4 z-10">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
          Step 3 of 3
        </div>
        <h1 className="font-serif text-[26px] font-normal leading-[1.1] tracking-[-0.015em] text-ink mb-1.5">
          What do you usually <em className="italic">buy</em>?
        </h1>
        <p className="font-serif text-[13px] italic text-ink-muted mb-4 leading-[1.4]">
          Pick what you keep at home. We'll learn the rest.
        </p>
        <div className="h-[3px] w-full rounded-full bg-border-hairline overflow-hidden">
          <div
            className="h-full bg-forest rounded-full transition-all duration-300"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      </div>

      {/* Item grid */}
      <div className="flex-1 overflow-y-auto pb-36">
        <div className="grid grid-cols-3 gap-3 px-4 py-4">
          {COMMON_ITEMS.map(item => {
            const isSelected = selectedItems.some(s => s.id === item.id)
            return (
              <button
                key={item.id}
                onClick={() => toggleItem(item)}
                className={`relative flex flex-col items-center gap-2 p-3 rounded-[16px] aspect-square transition-all ${
                  isSelected
                    ? 'border-[1.5px] border-forest bg-ok-bg'
                    : 'border border-border-sand bg-paper'
                }`}
              >
                <span className="text-[28px] leading-none">{item.emoji}</span>
                <span
                  className={
                    isSelected
                      ? 'font-serif text-[11px] font-medium text-forest text-center leading-tight'
                      : 'font-serif text-[11px] text-ink text-center leading-tight'
                  }
                >
                  {item.name}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Sticky footer — Skip + Continue, no minimum required */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] px-4 pb-6 pt-4 bg-cream border-t border-border-sand">
        <div className="flex gap-3">
          <button
            onClick={() => { setSelectedItems([]); handleComplete() }}
            disabled={loading}
            className="flex-1 rounded-[14px] border border-border-sand bg-paper py-3.5 text-[14px] font-semibold text-ink-muted disabled:opacity-60"
          >
            Skip
          </button>
          <button
            onClick={handleComplete}
            disabled={loading}
            className="flex-[2] rounded-[14px] bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em] disabled:opacity-60 transition-opacity"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 rounded-full border-2 border-forest-ink/30 border-t-forest-ink animate-spin" />
                Saving…
              </span>
            ) : (
              count > 0 ? `Continue (${count} items)` : 'Continue'
            )}
          </button>
        </div>
      </div>

    </div>
  )
}
